import { useState, useEffect, useCallback, useRef } from 'react';

const FOCUS_RESET_DELAY = 500;

// Helper function to get the maximum z-index of an element and its parents
const getMaxZIndex = (element) => {
  let currentElement = element;
  let maxZIndex = 0;

  while (currentElement && currentElement !== document.body) {
    const computedStyle = window.getComputedStyle(currentElement);
    const zIndex = parseInt(computedStyle.zIndex, 10);
    if (!isNaN(zIndex)) {
      maxZIndex = Math.max(maxZIndex, zIndex);
    }
    currentElement = currentElement.parentElement;
  }

  return maxZIndex;
};

// detects when focus attempts target higher z-index elements
// so we can know when to disable modal focus enforcement
export const useZIndexFocusDetection = (isModalOpen = false) => {
  const [allowExternalFocus, setAllowExternalFocus] = useState(false);
  const [modalElement, setModalElement] = useState(null);
  const resetTimeoutRef = useRef(null);
  const allowExternalFocusRef = useRef(false);

  // Use a callback ref to know when the modal element is attached
  const modalRef = useCallback((node) => {
    setModalElement(node);
  }, []);

  useEffect(() => {
    // Keep allowExternalFocus up to date
    allowExternalFocusRef.current = allowExternalFocus;
  }, [allowExternalFocus]);

  useEffect(() => {
    if (!isModalOpen || !modalElement) {
      setAllowExternalFocus(false);
      return;
    }

    const checkFocusAttemptZIndex = (event) => {
      const targetElement = event.target;

      // If focus is staying within the modal, no need to check
      if (modalElement.contains(targetElement)) {
        // Only reset if we were previously allowing external focus
        if (allowExternalFocusRef.current) {
          setAllowExternalFocus(false);
        }
        return;
      }

      // Get z-index of the target element and its parents
      const maxZIndex = getMaxZIndex(targetElement);

      // Get modal's z-index
      const modalZIndex =
        parseInt(window.getComputedStyle(modalElement).zIndex, 10) || 0;

      // If target element has higher z-index, allow external focus
      if (maxZIndex > modalZIndex) {
        setAllowExternalFocus(true);

        // Clear any existing reset timeout
        if (resetTimeoutRef.current) {
          clearTimeout(resetTimeoutRef.current);
        }

        // Set up a timeout to check if focus should be reset
        resetTimeoutRef.current = setTimeout(() => {
          const currentActive = document.activeElement;
          if (!currentActive || modalElement.contains(currentActive)) {
            setAllowExternalFocus(false);
          } else {
            // Check if current active element still has higher z-index
            const currentMaxZIndex = getMaxZIndex(currentActive);

            if (currentMaxZIndex <= modalZIndex) {
              setAllowExternalFocus(false);
            }
          }
        }, FOCUS_RESET_DELAY);
      }
    };

    // Use capture phase to intercept focus before enforcement
    document.addEventListener('focus', checkFocusAttemptZIndex, true);

    return () => {
      document.removeEventListener('focus', checkFocusAttemptZIndex, true);

      if (resetTimeoutRef.current) {
        clearTimeout(resetTimeoutRef.current);
      }
    };
  }, [isModalOpen, modalElement]);

  return {
    modalRef,
    modalContainerRef: modalRef, // Alias for compatibility
    shouldDisableEnforceFocus: allowExternalFocus,
  };
};
